import { TabSection } from './TabSection'
import { Toggle } from '../Toggle'
import { WorkingHoursDial } from './WorkingHoursDial'
import { TimezoneSelect } from './TimezoneSelect'
import type { ChannelConfigUpdate } from '../../types'
import styles from './FormFields.module.css'
import gridStyles from './NudgesTab.module.css'

const TIMEZONES: { value: string; label: string }[] = [
  { value: 'America/New_York',    label: 'Boston / New York (ET)' },
  { value: 'America/Chicago',     label: 'Chicago (CT)' },
  { value: 'America/Denver',      label: 'Denver (MT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'America/Sao_Paulo',   label: 'São Paulo (BRT)' },
  { value: 'Europe/Dublin',       label: 'Ireland / Dublin (IST)' },
  { value: 'Europe/London',       label: 'London (GMT/BST)' },
  { value: 'Europe/Paris',        label: 'Paris / Central Europe (CET)' },
  { value: 'Asia/Kolkata',        label: 'India (IST)' },
  { value: 'Asia/Singapore',      label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo',          label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney',    label: 'Sydney (AEST)' },
  { value: 'UTC',                 label: 'UTC' },
]

interface Props {
  draft: {
    nudgeEnabled: boolean
    escalationEnabled: boolean
    nudgeThresholdDays: number
    escalationAfterNudges: number
    timezone?: string | null
    workingHoursStart?: string | null
    workingHoursEnd?: string | null
  }
  onChange: (patch: Partial<ChannelConfigUpdate>) => void
}

export function NudgesTab({ draft, onChange }: Props) {
  return (
    <TabSection
      accent="amber"
      icon="schedule"
      title="Nudges"
      subtitle="Automated reminders for stale PRs, with optional escalation and working hours"
    >
      <div className={gridStyles.grid}>
        {/* Left — toggles */}
        <div className={gridStyles.col}>
          <Toggle
            label="Nudge reminders"
            hint="Remind the channel when a PR has been waiting too long for review"
            checked={draft.nudgeEnabled}
            onChange={v => onChange({ nudgeEnabled: v })}
          />
          <Toggle
            label="Manager escalation"
            hint="Notify a manager when a PR has received more than the set number of nudges with no response"
            checked={draft.escalationEnabled}
            onChange={v => onChange({ escalationEnabled: v })}
          />
        </div>

        {/* Right — thresholds + schedule */}
        <div className={gridStyles.col}>
          <div className={styles.field}>
            <label className={styles.label}>Nudge after</label>
            <div className={styles.numberRow}>
              <input
                type="number"
                min={1}
                max={30}
                value={draft.nudgeThresholdDays}
                onChange={e => onChange({ nudgeThresholdDays: Number(e.target.value) })}
                style={{ width: 90 }}
              />
              <span className={styles.numberLabel}>working days without review activity (weekends excluded)</span>
            </div>
          </div>

          <div className={`${styles.field} ${styles.fieldLast}`}>
            <label className={styles.label}>Escalate after</label>
            <div className={styles.numberRow}>
              <input
                type="number"
                min={1}
                max={20}
                value={draft.escalationAfterNudges}
                onChange={e => onChange({ escalationAfterNudges: Number(e.target.value) })}
                style={{ width: 90 }}
              />
              <span className={styles.numberLabel}>nudges sent by the bot</span>
            </div>
          </div>
        </div>
      </div>

      {/* Full-width schedule section */}
      <div className={gridStyles.scheduleSection}>
        <div>
          <div className={gridStyles.scheduleLabel}>Schedule</div>
          <div className={styles.field} style={{ marginTop: 16 }}>
            <label className={styles.label}>Timezone</label>
            <TimezoneSelect
              value={draft.timezone ?? 'UTC'}
              options={TIMEZONES}
              onChange={v => onChange({ timezone: v })}
            />
          </div>
        </div>
        <div>
          <div className={`${styles.field} ${styles.fieldLast}`} style={{ marginTop: 36 }}>
            <label className={styles.label}>Working hours</label>
            <WorkingHoursDial
              start={draft.workingHoursStart ?? '09:00'}
              end={draft.workingHoursEnd ?? '17:00'}
              onChange={(start, end) => onChange({ workingHoursStart: start, workingHoursEnd: end })}
            />
            <div className={styles.hint}>Nudges are only sent during these hours in the selected timezone</div>
          </div>
        </div>
      </div>
    </TabSection>
  )
}
